/**
 * Todo 状态管理 —— 使用 Zustand 创建全局状态仓库
 *
 * 【什么是 Zustand？】
 * Zustand 是一个轻量级的 React 状态管理库。它的名字来自德语，意思是"状态"。
 * 相比 Redux，Zustand 更简单、更轻量、样板代码更少：
 *
 *   Redux:  需要 action、reducer、dispatch、middleware……
 *   Zustand: 只需要一个 create() 函数
 *
 * 【为什么选择 Zustand 而不是 Redux 或 Context？】
 * - 比 Redux 简单：不需要 action/reducer/dispatch 的繁琐流程
 * - 比 Context 高效：Context 值变化时所有消费者都重渲染，
 *   Zustand 可以通过 selector 只订阅需要的数据
 * - 性能优秀：内部使用订阅-发布模式，精确控制重渲染范围
 * - 支持中间件：persist（持久化）、immer（不可变更新）、devtools（调试）
 *
 * 【Zustand 的核心模式】
 * ```ts
 * const useStore = create((set, get) => ({
 *   // 状态（数据）
 *   count: 0,
 *   // 操作（修改数据的方法）
 *   increment: () => set((state) => ({ count: state.count + 1 })),
 * }))
 *
 * // 在组件中使用
 * function Counter() {
 *   const count = useStore((state) => state.count);
 *   const increment = useStore((state) => state.increment);
 * }
 * ```
 *
 * - set: 更新状态的函数，类似 React 的 setState
 * - get: 读取当前状态的函数（用于在 action 中获取最新状态）
 * - set 接受一个函数 (state) => newState，返回新状态（部分更新会自动合并）
 *
 * 【什么是 persist 中间件？】
 * persist 中间件将 store 中的数据自动保存到 localStorage。
 * 页面刷新后，Zustand 会自动从 localStorage 恢复数据。
 * { name: "todo-storage" } 指定了 localStorage 中的键名。
 *
 * 【状态结构】
 * 本 store 管理以下状态：
 * - todos: 所有任务列表
 * - filter: 当前的过滤条件
 * - isFormExpanded: 表单是否展开
 * - isLoading: 是否正在加载
 * - error: 错误信息
 * - viewMode: 视图模式（列表/看板）
 */

import { Priority } from "@zero/types/enum";
import type {
  ActivityItem,
  CommentItem,
  CreateTodoParams,
  TodoFilter,
  TodoItem,
  UpdatableTodoStatus,
  UpdateTodoParams,
} from "@zero/types/todo";
import { create } from "zustand";
// persist 中间件：自动将状态同步到 localStorage
import { persist } from "zustand/middleware";
import { API_TODO_STATUS, TODO_STATUS } from "../api/adapters";
import { TodoApiService } from "../api/todoService";
import type { KanbanColumnId } from "../types/kanban";
import { isTodoCompleted, isTodoInProgress, statusWeight } from "../utils/todoRights";

export const DEFAULT_CATEGORIES = ["工作", "个人", "学习", "健康", "其他"] as const;

/**
 * TodoStore 接口 —— 定义仓库的完整类型
 *
 * 【为什么先定义接口？】
 * TypeScript 中先定义接口可以让：
 * 1. 编辑器提供更好的自动补全
 * 2. 类型检查更严格（缺少属性会报错）
 * 3. 文档化：接口就是最好的文档，说明 store 有哪些状态和操作
 *
 * 【状态和操作的分类】
 * - 状态（数据）：todos、filter、isLoading 等
 * - 异步操作：fetchTodos、addTodoAsync 等（调用后端 API）
 * - 同步操作：addTodo、updateTodo 等（仅修改本地状态，用于乐观更新）
 * - 计算属性：filteredTodos、activeCount 等（基于状态派生的值）
 */
interface TodoStore {
  /** 所有任务列表 */
  todos: TodoItem[];
  /** 当前的过滤条件（按状态、优先级、分类等筛选） */
  filter: TodoFilter;
  /** 搜索关键词 */
  search: string;
  /** 新建任务表单是否展开 */
  isFormExpanded: boolean;
  /** 是否正在加载（请求进行中） */
  isLoading: boolean;
  /** 错误信息（null 表示没有错误） */
  error: string | null;
  /** 视图模式：列表视图 or 看板视图 */
  viewMode: "list" | "kanban";
  /** 当前列表选择的任务 ID */
  selectedIds: string[];
  /** 用户创建的前端分类选项 */
  categoryOptions: string[];
  commentsByTodoId: Record<string, CommentItem[]>;
  activityByTodoId: Record<string, ActivityItem[]>;

  // ---- 异步 CRUD 操作（调用后端 API） ----
  fetchTodos: () => Promise<void>;
  fetchTodo: (id: string) => Promise<TodoItem | undefined>;
  addTodoAsync: (params: CreateTodoParams) => Promise<void>;
  updateTodoAsync: (id: string, params: UpdateTodoParams) => Promise<void>;
  deleteTodoAsync: (id: string) => Promise<void>;
  toggleTodoAsync: (id: string) => Promise<void>;
  toggleInProgress: (id: string) => Promise<void>;
  moveTodoToColumn: (id: string, columnId: KanbanColumnId) => Promise<void>;
  bulkUpdateStatus: (status: UpdatableTodoStatus) => Promise<void>;
  bulkDeleteSelected: () => Promise<void>;
  assignTodoAsync: (id: string, assigneeId: string) => Promise<void>;
  unassignTodoAsync: (id: string) => Promise<void>;
  fetchComments: (todoId: string) => Promise<void>;
  addCommentAsync: (todoId: string, content: string) => Promise<void>;
  updateCommentAsync: (todoId: string, commentId: string, content: string) => Promise<void>;
  deleteCommentAsync: (todoId: string, commentId: string) => Promise<void>;
  fetchActivity: (todoId: string) => Promise<void>;
  createCategory: (category: string) => void;
  renameCategory: (from: string, to: string) => Promise<void>;
  deleteCategory: (category: string) => Promise<void>;

  // ---- 同步 CRUD 操作（仅修改本地状态，保留用于乐观更新） ----
  addTodo: (params: CreateTodoParams) => void;
  updateTodo: (id: string, params: UpdateTodoParams) => void;
  deleteTodo: (id: string) => void;
  toggleTodo: (id: string) => void;

  // ---- 过滤操作 ----
  setFilter: (filter: TodoFilter) => void;
  setSearch: (query: string) => void;
  clearCompleted: () => void;

  // ---- UI 操作 ----
  setFormExpanded: (expanded: boolean) => void;
  toggleFormExpanded: () => void;
  setViewMode: (mode: "list" | "kanban") => void;
  toggleSelectedTodo: (id: string) => void;
  clearSelectedTodos: () => void;
  selectAllVisibleTodos: () => void;

  // ---- 拖拽排序 ----
  reorderTodos: (oldIndex: number, newIndex: number) => void;

  // ---- 计算属性（派生状态） ----
  filteredTodos: () => TodoItem[];
  activeCount: () => number;
  completedCount: () => number;
  categories: () => string[];
}

/**
 * 创建本地 Todo 对象的工厂函数
 *
 * 【为什么用工厂函数？】
 * 创建新任务时有很多字段需要初始化（id、时间戳、默认值等）。
 * 用工厂函数集中处理，避免在每个调用处重复写这些逻辑。
 *
 * 【crypto.randomUUID()】
 * 浏览器内置的 API，生成一个唯一的 ID（UUID v4 格式）。
 * 例如："9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d"
 * 在乐观更新场景中，先用本地生成的 ID 创建对象，
 * 后端返回后再替换为服务器分配的 ID。
 *
 * @param params - 创建任务的参数
 * @returns 一个新的 TodoItem 对象
 */
const createTodoItem = (params: CreateTodoParams): TodoItem => ({
  id: crypto.randomUUID(),
  title: params.title,
  description: params.description,
  status: TODO_STATUS.pending,
  priority: params.priority,
  dueDate: params.dueDate,
  category: params.category,
  tags: params.tags ?? [],
  ownerId: "",
  realmId: "",
  relationship: "owner",
  createdAt: new Date(),
  updatedAt: new Date(),
});

function compareTodoByPriority(a: TodoItem, b: TodoItem): number {
  const weight: Record<Priority, number> = {
    [Priority.Low]: 1,
    [Priority.Medium]: 2,
    [Priority.High]: 3,
    [Priority.Urgent]: 4,
  };
  return weight[a.priority] - weight[b.priority];
}

function compareTodoByStatus(a: TodoItem, b: TodoItem): number {
  return statusWeight(a.status) - statusWeight(b.status);
}

function compareOptionalDate(a?: Date, b?: Date): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.getTime() - b.getTime();
}

async function refreshTodoSnapshot(id: string): Promise<TodoItem | undefined> {
  try {
    return await TodoApiService.getTodo(id);
  } catch (_error) {
    return undefined;
  }
}

/**
 * 创建 Todo 全局状态仓库
 *
 * 【create<TodoStore>()() 的双层括号是什么意思？】
 * 第一层 () 是 TypeScript 泛型：create<TodoStore>
 * 第二层 () 是函数调用：create<TodoStore>()(...)
 * 这种写法是为了让 TypeScript 正确推断类型。
 *
 * 【persist 中间件的作用】
 * 将 store 数据自动保存到 localStorage，键名为 "todo-storage"。
 * 页面刷新后数据不会丢失。
 *
 * 【set 和 get 的区别】
 * - set(newState): 更新状态（类似 React 的 setState）
 * - get(): 获取当前最新状态（用于读取其他状态的最新值）
 *
 * 【不可变更新（Spread 操作符）】
 * Zustand 的 set() 会自动浅合并状态。
 * 但数组操作需要手动保证不可变性：
 * - 添加：[newItem, ...oldArray]（展开旧数组）
 * - 删除：array.filter(item => item.id !== targetId)
 * - 更新：array.map(item => item.id === targetId ? newItem : item)
 * 这些操作都创建新数组，不修改原数组。
 */
export const useTodoStore = create<TodoStore>()(
  persist(
    (set, get) => ({
      // ---- 初始状态 ----
      todos: [],
      filter: { status: "all" },
      search: "",
      isFormExpanded: false,
      isLoading: false,
      error: null,
      viewMode: "list",
      selectedIds: [],
      categoryOptions: [...DEFAULT_CATEGORIES],
      commentsByTodoId: {},
      activityByTodoId: {},

      // ============================================================
      // 异步操作（调用后端 API，然后更新本地状态）
      // ============================================================

      /**
       * 从服务器获取任务列表
       *
       * 【异步操作的通用模式】
       * 1. 设置 isLoading = true（显示加载指示器）
       * 2. 调用 API
       * 3. 成功：更新数据，设置 isLoading = false
       * 4. 失败：设置 error 信息，设置 isLoading = false
       */
      fetchTodos: async () => {
        set({ isLoading: true, error: null });
        try {
          // get() 获取最新状态（包括最新的 filter）
          const todos = await TodoApiService.listTodos(get().filter);
          set({ todos, isLoading: false });
        } catch (error) {
          set({
            isLoading: false,
            // 使用 instanceof 判断错误类型，安全地获取错误消息
            error: error instanceof Error ? error.message : "获取任务列表失败",
          });
        }
      },

      fetchTodo: async (id) => {
        set({ isLoading: true, error: null });
        try {
          const todo = await TodoApiService.getTodo(id);
          set((state) => {
            const exists = state.todos.some((item) => item.id === id);
            return {
              todos: exists ? state.todos.map((item) => (item.id === id ? todo : item)) : [todo, ...state.todos],
              isLoading: false,
            };
          });
          return todo;
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : "获取任务详情失败",
          });
          return undefined;
        }
      },

      /**
       * 异步创建新任务
       *
       * 【数组的不可变添加】
       * [newTodo, ...state.todos] 将新任务放在数组开头。
       * ...state.todos 展开运算符复制旧数组的所有元素到新数组中。
       * 原始的 state.todos 不会被修改（不可变性）。
       */
      addTodoAsync: async (params) => {
        set({ isLoading: true, error: null });
        try {
          const newTodo = await TodoApiService.createTodo(params);
          // 使用函数式 set，确保拿到最新状态
          set((state) => ({
            todos: [newTodo, ...state.todos], // 新任务添加到列表开头
            isLoading: false,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : "创建任务失败",
          });
          throw error; // 继续抛出，让调用方也能处理
        }
      },

      /**
       * 异步更新任务
       *
       * 【数组的不可变更新 —— map】
       * todos.map(todo => todo.id === id ? updatedTodo : todo)
       * 遍历数组，找到匹配的 todo 替换为新的，其余保持不变。
       * 返回一个全新的数组，不修改原数组。
       */
      updateTodoAsync: async (id, params) => {
        set({ isLoading: true, error: null });
        try {
          const updatedTodo = await TodoApiService.updateTodo(id, params);
          set((state) => ({
            todos: state.todos.map((todo) => (todo.id === id ? updatedTodo : todo)),
            isLoading: false,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : "更新任务失败",
          });
          throw error;
        }
      },

      /**
       * 异步删除任务
       *
       * 【数组的不可变删除 —— filter】
       * todos.filter(todo => todo.id !== id)
       * 返回一个不包含指定 id 的新数组。
       * filter 不修改原数组，而是创建一个新数组。
       */
      deleteTodoAsync: async (id) => {
        set({ isLoading: true, error: null });
        try {
          await TodoApiService.deleteTodo(id);
          set((state) => ({
            todos: state.todos.filter((todo) => todo.id !== id),
            selectedIds: state.selectedIds.filter((selectedId) => selectedId !== id),
            isLoading: false,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : "删除任务失败",
          });
          throw error;
        }
      },

      /**
       * 异步切换任务完成状态
       *
       * 【先读后写】
       * 先用 get() 找到当前任务的状态（completed 或 not），
       * 然后取反发送给后端。
       */
      toggleTodoAsync: async (id) => {
        // 先找到当前任务，获取它的完成状态
        const todo = get().todos.find((t) => t.id === id);
        if (!todo) return;
        if (isTodoCompleted(todo) || todo.relationship === "viewer") return;

        set({ isLoading: true, error: null });
        try {
          const updatedTodo = await TodoApiService.transitionTodo(id, TODO_STATUS.completed);
          set((state) => ({
            todos: state.todos.map((t) => (t.id === id ? updatedTodo : t)),
            isLoading: false,
          }));
        } catch (error) {
          const refreshed = await refreshTodoSnapshot(id);
          set({
            todos: refreshed ? get().todos.map((todo) => (todo.id === id ? refreshed : todo)) : get().todos,
            isLoading: false,
            error: error instanceof Error ? error.message : "操作失败",
          });
          throw error;
        }
      },

      /**
       * 异步移动任务到指定看板列
       *
       * 用于看板视图中的拖拽操作。
       */
      moveTodoToColumn: async (id, columnId) => {
        if (columnId === "todo") return;
        set({ isLoading: true, error: null });
        try {
          const updatedTodo = await TodoApiService.moveTodoToColumn(id, columnId);
          set((state) => ({
            todos: state.todos.map((t) => (t.id === id ? updatedTodo : t)),
            isLoading: false,
          }));
        } catch (error) {
          const refreshed = await refreshTodoSnapshot(id);
          set({
            todos: refreshed ? get().todos.map((todo) => (todo.id === id ? refreshed : todo)) : get().todos,
            isLoading: false,
            error: error instanceof Error ? error.message : "移动任务失败",
          });
          throw error;
        }
      },

      toggleInProgress: async (id) => {
        const todo = get().todos.find((t) => t.id === id);
        if (!todo) return;
        if (isTodoInProgress(todo) || isTodoCompleted(todo) || todo.relationship === "viewer") return;

        set({ isLoading: true, error: null });
        try {
          const updatedTodo = await TodoApiService.transitionTodo(id, TODO_STATUS.inProgress);
          set((state) => ({
            todos: state.todos.map((t) => (t.id === id ? updatedTodo : t)),
            isLoading: false,
          }));
        } catch (error) {
          const refreshed = await refreshTodoSnapshot(id);
          set({
            todos: refreshed ? get().todos.map((todo) => (todo.id === id ? refreshed : todo)) : get().todos,
            isLoading: false,
            error: error instanceof Error ? error.message : "操作失败",
          });
          throw error;
        }
      },

      bulkUpdateStatus: async (status) => {
        const selectedIds = get().selectedIds;
        if (selectedIds.length === 0) return;
        const apiStatus = status === "inProgress" ? API_TODO_STATUS.inProgress : API_TODO_STATUS[status];

        set({ isLoading: true, error: null });
        try {
          const todos = await TodoApiService.batchUpdateTodos(selectedIds.map((id) => ({ id, status: apiStatus })));
          set({ todos, selectedIds: [], isLoading: false });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : "批量更新失败",
          });
          throw error;
        }
      },

      bulkDeleteSelected: async () => {
        const selectedIds = get().selectedIds.filter((id) => {
          const todo = get().todos.find((item) => item.id === id);
          return todo?.relationship === "owner";
        });
        if (selectedIds.length === 0) return;

        set({ isLoading: true, error: null });
        try {
          await TodoApiService.deleteTodos(selectedIds);
          set((state) => ({
            todos: state.todos.filter((todo) => !selectedIds.includes(todo.id)),
            selectedIds: [],
            isLoading: false,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : "批量删除失败",
          });
          throw error;
        }
      },

      assignTodoAsync: async (id, assigneeId) => {
        set({ isLoading: true, error: null });
        try {
          const updatedTodo = await TodoApiService.assignTodo(id, assigneeId);
          const activity = await TodoApiService.listActivity(id);
          set((state) => ({
            todos: state.todos.map((todo) => (todo.id === id ? updatedTodo : todo)),
            activityByTodoId: { ...state.activityByTodoId, [id]: activity },
            isLoading: false,
          }));
        } catch (error) {
          const refreshed = await refreshTodoSnapshot(id);
          set({
            todos: refreshed ? get().todos.map((todo) => (todo.id === id ? refreshed : todo)) : get().todos,
            isLoading: false,
            error: error instanceof Error ? error.message : "指派任务失败",
          });
          throw error;
        }
      },

      unassignTodoAsync: async (id) => {
        set({ isLoading: true, error: null });
        try {
          const updatedTodo = await TodoApiService.unassignTodo(id);
          const activity = await TodoApiService.listActivity(id);
          set((state) => ({
            todos: state.todos.map((todo) => (todo.id === id ? updatedTodo : todo)),
            activityByTodoId: { ...state.activityByTodoId, [id]: activity },
            isLoading: false,
          }));
        } catch (error) {
          const refreshed = await refreshTodoSnapshot(id);
          set({
            todos: refreshed ? get().todos.map((todo) => (todo.id === id ? refreshed : todo)) : get().todos,
            isLoading: false,
            error: error instanceof Error ? error.message : "取消指派失败",
          });
          throw error;
        }
      },

      fetchComments: async (todoId) => {
        const todo = get().todos.find((item) => item.id === todoId);
        if (!todo) return;
        set({ isLoading: true, error: null });
        try {
          const comments = await TodoApiService.listComments(todo);
          set((state) => ({
            commentsByTodoId: { ...state.commentsByTodoId, [todoId]: comments },
            isLoading: false,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : "获取评论失败",
          });
          throw error;
        }
      },

      addCommentAsync: async (todoId, content) => {
        const todo = get().todos.find((item) => item.id === todoId);
        if (!todo) return;
        set({ isLoading: true, error: null });
        try {
          await TodoApiService.createComment(todo, content);
          const [comments, activity] = await Promise.all([
            TodoApiService.listComments(todo),
            TodoApiService.listActivity(todoId),
          ]);
          set((state) => ({
            commentsByTodoId: { ...state.commentsByTodoId, [todoId]: comments },
            activityByTodoId: { ...state.activityByTodoId, [todoId]: activity },
            isLoading: false,
          }));
        } catch (error) {
          const refreshed = await refreshTodoSnapshot(todoId);
          set({
            todos: refreshed ? get().todos.map((todo) => (todo.id === todoId ? refreshed : todo)) : get().todos,
            isLoading: false,
            error: error instanceof Error ? error.message : "创建评论失败",
          });
          throw error;
        }
      },

      updateCommentAsync: async (todoId, commentId, content) => {
        const todo = get().todos.find((item) => item.id === todoId);
        if (!todo) return;
        set({ isLoading: true, error: null });
        try {
          await TodoApiService.updateComment(todo, commentId, content);
          const [comments, activity] = await Promise.all([
            TodoApiService.listComments(todo),
            TodoApiService.listActivity(todoId),
          ]);
          set((state) => ({
            commentsByTodoId: { ...state.commentsByTodoId, [todoId]: comments },
            activityByTodoId: { ...state.activityByTodoId, [todoId]: activity },
            isLoading: false,
          }));
        } catch (error) {
          const refreshed = await refreshTodoSnapshot(todoId);
          set({
            todos: refreshed ? get().todos.map((todo) => (todo.id === todoId ? refreshed : todo)) : get().todos,
            isLoading: false,
            error: error instanceof Error ? error.message : "更新评论失败",
          });
          throw error;
        }
      },

      deleteCommentAsync: async (todoId, commentId) => {
        const todo = get().todos.find((item) => item.id === todoId);
        if (!todo) return;
        set({ isLoading: true, error: null });
        try {
          await TodoApiService.deleteComment(todoId, commentId);
          const [comments, activity] = await Promise.all([
            TodoApiService.listComments(todo),
            TodoApiService.listActivity(todoId),
          ]);
          set((state) => ({
            commentsByTodoId: { ...state.commentsByTodoId, [todoId]: comments },
            activityByTodoId: { ...state.activityByTodoId, [todoId]: activity },
            isLoading: false,
          }));
        } catch (error) {
          const refreshed = await refreshTodoSnapshot(todoId);
          set({
            todos: refreshed ? get().todos.map((todo) => (todo.id === todoId ? refreshed : todo)) : get().todos,
            isLoading: false,
            error: error instanceof Error ? error.message : "删除评论失败",
          });
          throw error;
        }
      },

      fetchActivity: async (todoId) => {
        set({ isLoading: true, error: null });
        try {
          const activity = await TodoApiService.listActivity(todoId);
          set((state) => ({
            activityByTodoId: { ...state.activityByTodoId, [todoId]: activity },
            isLoading: false,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : "获取活动失败",
          });
          throw error;
        }
      },

      createCategory: (category) => {
        const nextCategory = category.trim();
        if (!nextCategory) return;

        set((state) => {
          if (state.categories().includes(nextCategory)) return {};
          return { categoryOptions: [...state.categoryOptions, nextCategory] };
        });
      },

      renameCategory: async (from, to) => {
        const nextCategory = to.trim();
        if (!from || !nextCategory || from === nextCategory) return;

        const affectedTodos = get().todos.filter((todo) => todo.category === from);
        if (affectedTodos.length === 0) {
          set((state) => ({
            categoryOptions: state.categoryOptions.map((category) => (category === from ? nextCategory : category)),
            filter: state.filter.category === from ? { ...state.filter, category: nextCategory } : state.filter,
          }));
          return;
        }

        set({ isLoading: true, error: null });
        try {
          const updatedTodos = await Promise.all(
            affectedTodos.map((todo) =>
              TodoApiService.updateTodo(todo.id, { category: nextCategory, tags: todo.tags }),
            ),
          );
          set((state) => ({
            todos: state.todos.map((todo) => updatedTodos.find((updated) => updated.id === todo.id) ?? todo),
            categoryOptions: state.categoryOptions.map((category) => (category === from ? nextCategory : category)),
            filter: state.filter.category === from ? { ...state.filter, category: nextCategory } : state.filter,
            isLoading: false,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : "分类重命名失败",
          });
          throw error;
        }
      },

      deleteCategory: async (category) => {
        if (!category || category === "其他") return;

        const affectedTodos = get().todos.filter((todo) => todo.category === category);
        if (affectedTodos.length === 0) {
          set((state) => ({
            categoryOptions: state.categoryOptions.filter((option) => option !== category),
            filter: state.filter.category === category ? { ...state.filter, category: undefined } : state.filter,
          }));
          return;
        }

        set({ isLoading: true, error: null });
        try {
          const updatedTodos = await Promise.all(
            affectedTodos.map((todo) => TodoApiService.updateTodo(todo.id, { category: "其他", tags: todo.tags })),
          );
          set((state) => ({
            todos: state.todos.map((todo) => updatedTodos.find((updated) => updated.id === todo.id) ?? todo),
            categoryOptions: state.categoryOptions.filter((option) => option !== category),
            filter: state.filter.category === category ? { ...state.filter, category: undefined } : state.filter,
            isLoading: false,
          }));
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : "分类删除失败",
          });
          throw error;
        }
      },

      // ============================================================
      // 同步操作（仅修改本地状态，用于乐观更新）
      // ============================================================

      /**
       * 同步添加任务（乐观更新）
       *
       * 不调用后端 API，直接在本地创建任务对象并添加到列表。
       * 用于"先更新 UI，后发请求"的乐观更新场景。
       */
      addTodo: (params) => {
        const newTodo = createTodoItem(params);
        set((state) => ({ todos: [newTodo, ...state.todos] }));
      },

      /**
       * 同步更新任务（乐观更新）
       *
       * 【展开运算符合并对象】
       * { ...todo, ...params, updatedAt: new Date() }
       * 先展开旧的 todo 所有字段，再用 params 覆盖部分字段，
       * 最后设置 updatedAt 为当前时间。
       * 后面的属性会覆盖前面的同名属性。
       */
      updateTodo: (id, params) => {
        set((state) => ({
          todos: state.todos.map((todo) => (todo.id === id ? { ...todo, ...params, updatedAt: new Date() } : todo)),
        }));
      },

      /** 同步删除任务（乐观更新） */
      deleteTodo: (id) => {
        set((state) => ({
          todos: state.todos.filter((todo) => todo.id !== id),
        }));
      },

      /** 同步切换完成状态（乐观更新） */
      toggleTodo: (id) => {
        set((state) => ({
          todos: state.todos.map((todo) =>
            todo.id === id && !isTodoCompleted(todo)
              ? { ...todo, status: TODO_STATUS.completed, updatedAt: new Date() }
              : todo,
          ),
        }));
      },

      // ============================================================
      // 过滤操作
      // ============================================================

      setFilter: (filter) => {
        set({ filter });
      },

      setSearch: (query) => {
        set((state) => ({ search: query, filter: { ...state.filter, search: query || undefined } }));
      },

      clearCompleted: () => {
        set((state) => ({
          todos: state.todos.filter((todo) => !isTodoCompleted(todo)),
        }));
      },

      // ============================================================
      // UI 操作
      // ============================================================

      /** 设置表单展开状态 */
      setFormExpanded: (expanded) => {
        set({ isFormExpanded: expanded });
      },

      /** 切换表单展开/收起 */
      toggleFormExpanded: () => {
        set((state) => ({ isFormExpanded: !state.isFormExpanded }));
      },

      /** 设置视图模式（列表 or 看板） */
      setViewMode: (mode) => {
        set({ viewMode: mode });
      },

      toggleSelectedTodo: (id) => {
        set((state) => ({
          selectedIds: state.selectedIds.includes(id)
            ? state.selectedIds.filter((selectedId) => selectedId !== id)
            : [...state.selectedIds, id],
        }));
      },

      clearSelectedTodos: () => {
        set({ selectedIds: [] });
      },

      selectAllVisibleTodos: () => {
        const visibleIds = get()
          .filteredTodos()
          .map((todo) => todo.id);
        const selectedIds = get().selectedIds;
        const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
        set((state) => ({
          selectedIds: allVisibleSelected
            ? state.selectedIds.filter((id) => !visibleIds.includes(id))
            : Array.from(new Set([...state.selectedIds, ...visibleIds])),
        }));
      },

      // ============================================================
      // 拖拽排序
      // ============================================================

      /**
       * 重新排列任务顺序
       *
       * 【splice 实现移动元素】
       * 1. [...state.todos] 先复制数组（不修改原数组）
       * 2. splice(oldIndex, 1) 从旧位置取出元素
       * 3. splice(newIndex, 0, removed) 插入到新位置
       *
       * 这是一种常见的数组元素移动模式。
       *
       * @param oldIndex - 元素原来的位置
       * @param newIndex - 元素要移动到的位置
       */
      reorderTodos: (oldIndex, newIndex) => {
        set((state) => {
          // 复制数组，确保不可变性
          const newTodos = [...state.todos];
          // 从旧位置移除元素，返回被移除的数组
          const [removed] = newTodos.splice(oldIndex, 1);
          // 将元素插入到新位置
          newTodos.splice(newIndex, 0, removed);
          return { todos: newTodos };
        });
      },

      // ============================================================
      // 计算属性（派生状态）
      // ============================================================

      /**
       * 根据当前过滤条件筛选任务
       *
       * 【为什么用方法而不是直接计算？】
       * 因为过滤条件（filter）可能随时变化，
       * 每次调用都根据最新的 filter 重新计算。
       *
       * 【过滤逻辑】
       * 使用 Array.filter() 的链式条件判断：
       * - 所有条件都通过才保留该任务
       * - 每个条件检查前先判断过滤条件是否存在
       */
      filteredTodos: () => {
        const { todos, filter, search } = get();
        const filtered = todos.filter((todo) => {
          if (filter.status === "active" && (isTodoCompleted(todo) || todo.status === "archived")) return false;
          if (filter.status !== "all" && filter.status !== "active" && todo.status !== filter.status) return false;
          if (filter.ownership === "ownedByMe" && todo.relationship !== "owner") return false;
          if (filter.ownership === "assignedToMe" && todo.relationship !== "assignee") return false;

          if (filter.priority && todo.priority !== filter.priority) return false;

          if (filter.category && todo.category !== filter.category) return false;

          if (search) {
            const q = search.toLowerCase();
            const searchable = [todo.title, todo.description, todo.category, ...todo.tags]
              .filter((value): value is string => Boolean(value))
              .join(" ")
              .toLowerCase();
            return searchable.includes(q);
          }

          return true;
        });

        const sortBy = filter.sortBy ?? "createdAt";
        const direction = filter.sortDirection ?? "desc";
        const multiplier = direction === "asc" ? 1 : -1;

        return [...filtered].sort((a, b) => {
          let result = 0;
          if (sortBy === "createdAt") result = a.createdAt.getTime() - b.createdAt.getTime();
          if (sortBy === "updatedAt") result = a.updatedAt.getTime() - b.updatedAt.getTime();
          if (sortBy === "dueDate") result = compareOptionalDate(a.dueDate, b.dueDate);
          if (sortBy === "priority") result = compareTodoByPriority(a, b);
          if (sortBy === "status") result = compareTodoByStatus(a, b);
          return result * multiplier;
        });
      },

      /** 获取未完成任务的数量 */
      activeCount: () => {
        return get().todos.filter((todo) => !isTodoCompleted(todo) && todo.status !== "archived").length;
      },

      /** 获取已完成任务的数量 */
      completedCount: () => {
        return get().todos.filter(isTodoCompleted).length;
      },

      categories: () => {
        return Array.from(
          new Set([...get().categoryOptions, ...get().todos.map((todo) => todo.category)].filter(Boolean)),
        ).sort((a, b) => a.localeCompare(b, "zh-CN"));
      },
    }),
    {
      name: "todo-storage",
      partialize: (state: TodoStore) =>
        ({ viewMode: state.viewMode, categoryOptions: state.categoryOptions }) as Partial<TodoStore>,
    },
  ),
);

// ============================================================
// Selectors（选择器）
// ============================================================

/**
 * 【什么是 Selector？】
 * Selector 是从 store 中提取特定数据的函数。
 * 好处是：
 * 1. 组件只订阅需要的数据，避免不必要的重渲染
 * 2. 提取逻辑集中在一处，方便复用和维护
 *
 * 【用法】
 * ```tsx
 * // 在组件中通过 selector 订阅特定的数据
 * const todos = useTodoStore(selectTodos);
 * // 只有 todos 变化时组件才会重渲染
 * ```
 *
 * 【为什么用函数而不是直接访问？】
 * 如果写 const todos = useTodoStore((s) => s.todos)，
 * 每次渲染都会创建新的箭头函数，虽然结果一样但引用不同。
 * 提前定义 selector 函数可以避免这个问题，确保引用稳定。
 */
export const selectTodos = (state: TodoStore) => state.todos;
/** 选择过滤后的任务列表 */
export const selectFilteredTodos = (state: TodoStore) => state.filteredTodos();
/** 选择未完成任务的数量 */
export const selectActiveCount = (state: TodoStore) => state.activeCount();
/** 选择已完成任务的数量 */
export const selectCompletedCount = (state: TodoStore) => state.completedCount();
/** 选择当前过滤条件 */
export const selectFilter = (state: TodoStore) => state.filter;
